import React, { memo, useCallback, useEffect, useState } from 'react';
import { hooks, signaling } from '@lifesize/clients.sdk';
import Tooltip from '@material-ui/core/Tooltip';
import { Fab, makeStyles } from '@material-ui/core';
import { ReactComponent as RecordIcon } from '@lifesize/ux-assets/vectors/svgIcons/record.svg';
import { ReactComponent as RecordStopIcon } from '@lifesize/ux-assets/vectors/svgIcons/record-stop.svg';
import { colors } from '@lifesize/ux-assets';
import _memoize from 'lodash/memoize';
import { injectIntl } from 'react-intl';

const messages = {
  startRecording: {
    id: 'components.incall.controls.record.tooltip.on',
    defaultMessage: 'Start recording'
  },
  stopRecording: {
    id: 'components.incall.controls.record.tooltip.off',
    defaultMessage: 'Stop recording'
  },
  recordingStarted: {
    id: 'components.incall.footer.recording.cantStopUnknown',
    defaultMessage: 'Recording started'
  },
  recordingStartedByUser: {
    id: 'components.incall.footer.recording.cantStop',
    defaultMessage: 'Recording started by {name}'
  },
  autoRecordingEnabled: {
    id: 'components.incall.footer.recording.autoRecording',
    defaultMessage: 'The recording cannot be stopped'
  }
};

interface RecordingButtonProps {
  intl: { formatMessage: Function };
}

const RecordingButton = (props: RecordingButtonProps) => {
  const { intl } = props;
  const isRecordingActive = hooks.useIsRecordingActive();
  const [isAnotherRecording, setIsAnotherRecording] = useState(false);
  const isConnected = hooks.useCallConnected();
  const canStopRecording = hooks.useCanStopRecording();
  const isAutoRecording = hooks.useIsAutoRecording();
  const inCall = hooks.useInCall();
  const isDisabled = !inCall || !isConnected || isAnotherRecording;
  const isGuest = hooks.useUserInfo()?.isGuest;
  const recorderDisplayName = hooks.useRecordingStartedBy()?.displayName;
  const accountSettings = hooks.useAccountSettings();
  const recordingSetting = accountSettings?.recordingEnabled;
  const recordingEnabled = !isGuest && recordingSetting;
  const classes = useStyles({ ...getStyleOverride(isRecordingActive, isAnotherRecording) });

  const getTooltipMessage = useCallback(
    (
      canStopRecording: boolean,
      recorder,
      isRecording: boolean,
      autoRecording: boolean
    ): NonNullable<React.ReactNode> => {
      if (isRecording) {
        if (canStopRecording) {
          return intl.formatMessage(messages.stopRecording);
        } else if (autoRecording) {
          return intl.formatMessage(messages.autoRecordingEnabled);
        } else if (!!recorder) {
          return intl.formatMessage(messages.recordingStartedByUser, { name: recorder });
        } else {
          return intl.formatMessage(messages.recordingStarted);
        }
      } else {
        return intl.formatMessage(messages.startRecording);
      }
    },
    [intl]
  );

  useEffect(() => {
    setIsAnotherRecording(isRecordingActive && !!recorderDisplayName && !canStopRecording);
  }, [isRecordingActive, setIsAnotherRecording, recorderDisplayName, canStopRecording]);

  const handleClick = () => {
    if (!isAnotherRecording) {
      if (isRecordingActive) {
        signaling.stopRecording();
      } else {
        signaling.startRecording();
      }
    }
  };

  // This user does not have the recording feature available
  if (!recordingEnabled) return <></>;

  return (
    <Tooltip
      title={getTooltipMessage(canStopRecording, recorderDisplayName, isRecordingActive, isAutoRecording)}
      placement="top"
      arrow={true}
    >
      <span data-testid="recording-btn">
        <Fab classes={classes} disabled={isDisabled} disableFocusRipple={true} onClick={handleClick} size="medium">
          {isRecordingActive ? (
            <RecordStopIcon data-testid="recordStopIcon" />
          ) : (
            <RecordIcon data-testid="recordStartIcon" />
          )}
        </Fab>
      </span>
    </Tooltip>
  );
};

/** Style selectors that can be dynamically modified */
interface StyleProps {
  opacity: number;
  color: string;
  borderColor: string;
  cursor: string;
}

const getStyleOverride = _memoize(
  (isRecording: boolean, isAnotherRecording: boolean): StyleProps => {
    return {
      borderColor: isRecording ? colors.alertRed : colors.white,
      color: isRecording ? colors.alertRed : colors.white,
      cursor: isAnotherRecording ? 'default' : 'hand',
      opacity: isRecording ? 1.0 : 0.6
    };
  },
  (isRecording: boolean, isAnotherRecording: boolean) => {
    return `${isRecording}${isAnotherRecording}`;
  }
);

const useStyles = makeStyles(() => ({
  root: {
    backgroundColor: 'transparent',
    border: (btnStyle: StyleProps) => `2px solid ${btnStyle.borderColor}`,
    boxShadow: 'none',
    color: (currentStyle: StyleProps) => currentStyle.color,
    cursor: (currentStyle: StyleProps) => currentStyle.cursor,
    margin: 0,
    opacity: (btnStyle: StyleProps) => btnStyle.opacity,
    '&:hover': {
      backgroundColor: 'transparent',
      border: (btnStyle: StyleProps) => `2px solid ${btnStyle.borderColor}`,
      cursor: (currentStyle: StyleProps) => currentStyle.cursor,
      opacity: 1.0
    },
    '& svg': {
      height: 40,
      width: 'auto'
    }
  },
  disabled: {
    color: `${colors.sentinel} !important`,
    borderColor: `${colors.sentinel} !important`,
    backgroundColor: `${colors.squidInk} !important`,
    opacity: `1 !important`,
    '& svg': {
      color: (currentStyle: StyleProps) => currentStyle.color,
      opacity: (btnStyle: StyleProps) => btnStyle.opacity
    }
  }
}));

export default memo(injectIntl(RecordingButton));
